import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import WeekRangeSelect from '../../../Components/WeekRangeSelect';
import PageHeader from '../shared/PageHeader';
import FilterCard from '../shared/FilterCard';
import { formatDisplayWeek, getWeekDetails } from '../../../helper';
import { sheetEmbedUrl, sheetTabName } from '../../../config/deliverySheet';

/* Finance → Delivery Sheet (2026-10-28, phases 1–2 of the user's OneDrive plan).
 *
 * Shows the OneDrive workbook "New Delivery.xlsx" (options from the user's embed code — see
 * config/deliverySheet.js; typing there is never saved to OneDrive), on the tab of the chosen week (tabs
 * are named after the week's Monday, ddmmyy). It is Microsoft's own viewer in an <iframe>: the
 * system downloads and stores nothing. Sync reloads the viewer, so OneDrive serves its latest
 * saved version; "Last synced" is when that reload finished.
 *
 * The viewer scrolls inside itself — it is Microsoft's page, so this is the one inner scroll box
 * the "page is the only scroll" rule (T1.47) cannot remove. The system cannot see inside it either,
 * so it cannot tell whether a week's tab exists (OneDrive then shows "Unable to Display Named Item";
 * OK there shows the workbook).
 * Phase 3 ("update on system") will read cell values separately, through Microsoft Graph.
 */
export default function DeliverySheet() {
    const [week, setWeek] = useState(getWeekDetails().weekId);
    const [bust, setBust] = useState(() => Date.now());
    const [loading, setLoading] = useState(true);
    const [lastSynced, setLastSynced] = useState(null);
    const frameRef = useRef(null);

    const src = useMemo(() => sheetEmbedUrl(week, bust), [week, bust]);
    const tab = sheetTabName(week);

    // Loading is switched on exactly where the address changes (a new week, or Sync) — each change
    // remounts the viewer (key={src}), so its onLoad always follows. If OneDrive never answers, the
    // button comes back after 30s rather than staying on "Syncing…".
    const reload = (nextWeek) => {
        setLoading(true);
        if (nextWeek !== undefined) setWeek(nextWeek); else setBust(Date.now());
    };
    useEffect(() => {
        if (!loading) return undefined;
        const t = setTimeout(() => setLoading(false), 30000);
        return () => clearTimeout(t);
    }, [loading, src]);

    const sync = () => reload();
    const onLoaded = () => {
        setLoading(false);
        setLastSynced(new Date());
    };

    return (
        <Container>
            <PageHeader
                title="Delivery Sheet"
                subtitle={tab ? `${formatDisplayWeek(week)} · OneDrive tab ${tab}` : 'OneDrive delivery sheet'}
            />

            <FilterCard>
                <Row style={{ alignItems: 'flex-end' }}>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Week</Form.Label>
                            <WeekRangeSelect
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                value={week}
                                onChange={(evt) => { if (evt.target.value && evt.target.value !== week) reload(evt.target.value); }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md="auto">
                        <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <Button
                                className="add-button"
                                variant="contained"
                                size="small"
                                onClick={sync}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SyncIcon fontSize="small" />}
                                aria-label="Sync the sheet from OneDrive"
                            >
                                {loading ? 'Syncing…' : 'Sync'}
                            </Button>
                            <span style={{ fontSize: 13, color: '#585f6c' }} aria-live="polite">
                                {lastSynced ? `Last synced ${lastSynced.toLocaleTimeString('en-GB')}` : 'Loading from OneDrive…'}
                            </span>
                        </div>
                    </Col>
                </Row>
            </FilterCard>

            <div style={{ position: 'relative', margin: '0 0 12px', border: '1px solid var(--pc-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                {loading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                        <CircularProgress size={28} />
                    </div>
                )}
                <iframe
                    ref={frameRef}
                    key={src}
                    title={`Delivery sheet, tab ${tab || 'first'}`}
                    src={src}
                    onLoad={onLoaded}
                    style={{ display: 'block', width: '100%', height: 'max(480px, 75vh)', border: 0 }}
                />
            </div>
            <p style={{ fontSize: 12, color: '#585f6c', margin: '0 0 24px' }}>
                The OneDrive sheet, live. Anything typed here is not saved to OneDrive. If this week
                has no tab yet, OneDrive shows "Unable to Display Named Item" — press OK to see the
                workbook, or pick another week.
            </p>
        </Container>
    );
}
